// src/app/services/hooks/useSelectionOrderService.ts
import useFetch from '../../../hooks/useFetch';
import { ServiceEvents } from '../shared/interfaces';
import { useState } from 'react';
import { EndPoints } from '../EndPoints';
import {
  SelectionOrder,
  SelectionOrderFilters,
  SelectionOrderStorePayload,
  SelectionOrderUpdatePayload,
  SelectionOrderLoadBatchPayload,
} from '../../models/SelectionOrder';

const useSelectionOrderService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();

  // flags existentes
  const [fetchingGetSelectionOrders, setFetchingGetSelectionOrders] = useState(false);
  const [fetchingCreateSelectionOrder, setFetchingCreateSelectionOrder] = useState(false);
  const [fetchingStoreSelectionOrder, setFetchingStoreSelectionOrder] = useState(false);
  const [fetchingEditSelectionOrder, setFetchingEditSelectionOrder] = useState(false);
  const [fetchingUpdateSelectionOrder, setFetchingUpdateSelectionOrder] = useState(false);
  const [fetchingDeleteSelectionOrder, setFetchingDeleteSelectionOrder] = useState(false);
  const [fetchingActiveSelectionOrder, setFetchingActiveSelectionOrder] = useState(false);
  const [fetchingShowSelectionOrder, setFetchingShowSelectionOrder] = useState(false);

  // flags batches
  const [fetchingGetOrderBatches, setFetchingGetOrderBatches] = useState(false);
  const [fetchingCreateOrderBatchOptions, setFetchingCreateOrderBatchOptions] = useState(false);
  const [fetchingStoreOrderBatch, setFetchingStoreOrderBatch] = useState(false);
  const [fetchingDeleteOrderBatch, setFetchingDeleteOrderBatch] = useState(false);
  const [fetchingStoreOrderBatchByBarcode, setFetchingStoreOrderBatchByBarcode] = useState(false);
  const [fetchingUpdateBatchStatus, setFetchingUpdateBatchStatus] = useState(false);
  
  // flags para cargar lotes (PENDING -> LOADED)
  const [fetchingCreateLoadBatchOptions, setFetchingCreateLoadBatchOptions] = useState(false);
  const [fetchingStoreLoadBatch, setFetchingStoreLoadBatch] = useState(false);

  // flags collectors
  const [fetchingGetOrderCollectors, setFetchingGetOrderCollectors] = useState(false);
  const [fetchingCreateOrderCollectorOptions, setFetchingCreateOrderCollectorOptions] = useState(false);
  const [fetchingStoreOrderCollector, setFetchingStoreOrderCollector] = useState(false);
  const [fetchingDeleteOrderCollector, setFetchingDeleteOrderCollector] = useState(false);

  // flag de historial
  const [fetchingGetOrderHistory, setFetchingGetOrderHistory] = useState(false);

  // flags destination batches
  const [fetchingGetDestinationBatches, setFetchingGetDestinationBatches] = useState(false);
  const [fetchingCreateDestinationBatchOptions, setFetchingCreateDestinationBatchOptions] = useState(false);
  const [fetchingStoreDestinationBatch, setFetchingStoreDestinationBatch] = useState(false);
  const [fetchingDeleteDestinationBatch, setFetchingDeleteDestinationBatch] = useState(false);

  // flag incidencias
  const [fetchingGetOrderIncidents, setFetchingGetOrderIncidents] = useState(false);
  const [fetchingStoreOrderIncident, setFetchingStoreOrderIncident] = useState(false);
  const [fetchingUpdateOrderIncident, setFetchingUpdateOrderIncident] = useState(false);
  const [fetchingDeleteOrderIncident, setFetchingDeleteOrderIncident] = useState(false);


  // ---------- ORDERS ----------
  const getSelectionOrders = (filters: SelectionOrderFilters, events: ServiceEvents = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        query.set(k, String(v));
      }
    });
    const queryString = query.toString();

    return doGet({
      ...events,
      url: `${EndPoints.SELECTION_ORDERS.GET_SELECTION_ORDERS}${queryString ? `?${queryString}` : ''}`,
      setFetching: setFetchingGetSelectionOrders,
    });
  };

  const createSelectionOrder = (events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.SELECTION_ORDERS.CREATE_SELECTION_ORDER,
      setFetching: setFetchingCreateSelectionOrder,
    });

  const storeSelectionOrder = (data: SelectionOrderStorePayload, events: ServiceEvents = {}) =>
    doPost({
      ...events,
      url: EndPoints.SELECTION_ORDERS.STORE_SELECTION_ORDER,
      body: data,
      setFetching: setFetchingStoreSelectionOrder,
    });

  const editSelectionOrder = (id: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.SELECTION_ORDERS.EDIT_SELECTION_ORDER.replace(':id', id.toString()),
      setFetching: setFetchingEditSelectionOrder,
    });

  const updateSelectionOrder = (
    id: number,
    data: SelectionOrderUpdatePayload,
    events: ServiceEvents = {}
  ) =>
    doPut({
      ...events,
      url: EndPoints.SELECTION_ORDERS.UPDATE_SELECTION_ORDER.replace(':id', id.toString()),
      body: data,
      setFetching: setFetchingUpdateSelectionOrder,
    });

  const deleteSelectionOrder = (id: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.SELECTION_ORDERS.DELETE_SELECTION_ORDER.replace(':id', id.toString()),
      setFetching: setFetchingDeleteSelectionOrder,
    });

  const activeSelectionOrder = (id: number, active: boolean, events: ServiceEvents = {}) =>
    doPost({
      ...events,
      url: EndPoints.SELECTION_ORDERS.ACTIVE_SELECTION_ORDER.replace(':id', id.toString()),
      body: { active },
      setFetching: setFetchingActiveSelectionOrder,
    });

  // ---------- BATCHES dentro de la orden ----------
  const getOrderBatches = (orderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.SELECTION_ORDER_BATCHES.GET_SELECTION_ORDER_BATCHES.replace(':id', orderId.toString()),
      setFetching: setFetchingGetOrderBatches,
    });

  const createOrderBatchOptions = (orderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.SELECTION_ORDER_BATCHES.CREATE_SELECTION_ORDER_BATCH.replace(':id', orderId.toString()),
      setFetching: setFetchingCreateOrderBatchOptions,
    });

  /**
   * body esperado por el backend:
   * { batch_id: number, used_quantity?: number }
   */
  const storeOrderBatch = (
    orderId: number,
    data: { batch_id: number; used_quantity?: number },
    events: ServiceEvents = {}
  ) =>
    doPost({
      ...events,
      url: EndPoints.SELECTION_ORDER_BATCHES.STORE_SELECTION_ORDER_BATCH.replace(':id', orderId.toString()),
      body: data,
      setFetching: setFetchingStoreOrderBatch,
    });

  const deleteOrderBatch = (linkId: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.SELECTION_ORDER_BATCHES.DELETE_SELECTION_ORDER_BATCH.replace(':link_id', linkId.toString()),
      setFetching: setFetchingDeleteOrderBatch,
    });

  /**
   * Actualiza el estado de reserva de un lote (RESERVED/RELEASED)
   * Solo aplica a lotes en estado PENDING
   */
    const updateBatchStatus = (
    linkId: number,
    status: string,
    events: ServiceEvents = {}
    ) =>
      doPut({
        ...events,
        url: EndPoints.SELECTION_ORDER_BATCHES.UPDATE_BATCH_STATUS.replace(':link_id', linkId.toString()),
        body: { status },
        requestType: 'json',
        setFetching: setFetchingUpdateBatchStatus,
      });

  // ---------- CARGAR LOTES (PENDING -> LOADED) ----------
  
  /**
   * Obtiene las opciones para cargar un lote (productos disponibles)
   */
  const createLoadBatchOptions = (orderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.SELECTION_ORDER_BATCHES.CREATE_LOAD_BATCH.replace(':id', orderId.toString()),
      setFetching: setFetchingCreateLoadBatchOptions,
    });

  /**
   * Carga un lote pendiente (cambia estado a LOADED)
   * body esperado: { link_id, product_id, unit_id, used_quantity }
   */
  const storeLoadBatch = (
    orderId: number,
    data: SelectionOrderLoadBatchPayload,
    events: ServiceEvents = {}
  ) =>
    doPost({
      ...events,
      url: EndPoints.SELECTION_ORDER_BATCHES.STORE_LOAD_BATCH.replace(':id', orderId.toString()),
      body: data,
      setFetching: setFetchingStoreLoadBatch,
    });

  // ---------- COLLECTORS dentro de la orden ----------
  const getOrderCollectors = (orderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.SELECTION_ORDER_COLLECTORS.GET_SELECTION_ORDER_COLLECTORS.replace(':id', orderId.toString()),
      setFetching: setFetchingGetOrderCollectors,
    });

  const createOrderCollectorOptions = (orderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.SELECTION_ORDER_COLLECTORS.CREATE_SELECTION_ORDER_COLLECTOR.replace(':id', orderId.toString()),
      setFetching: setFetchingCreateOrderCollectorOptions,
    });

  /**
   * body esperado por el backend:
   * { user_id: number, is_lead?: boolean }
   */
  const storeOrderCollector = (
    orderId: number,
    data: { user_id: number; is_lead?: boolean },
    events: ServiceEvents = {}
  ) =>
    doPost({
      ...events,
      url: EndPoints.SELECTION_ORDER_COLLECTORS.STORE_SELECTION_ORDER_COLLECTOR.replace(':id', orderId.toString()),
      body: data,
      setFetching: setFetchingStoreOrderCollector,
    });

  const deleteOrderCollector = (linkId: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.SELECTION_ORDER_COLLECTORS.DELETE_SELECTION_ORDER_COLLECTOR.replace(':link_id', linkId.toString()),
      setFetching: setFetchingDeleteOrderCollector,
    });

  const showSelectionOrder = (id: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.SELECTION_ORDERS.SHOW_SELECTION_ORDER.replace(':id', id.toString()),
      setFetching: setFetchingShowSelectionOrder,
    });

    // HISTORIAL de la orden
  const getSelectionOrderHistory = (
    id: number,
    params: { page?: number; page_size?: number; event_type?: string; date_from?: string; date_to?: string } = {},
    events: ServiceEvents = {}
  ) => {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.page_size) q.set('page_size', String(params.page_size));
    if (params.event_type) q.set('event_type', params.event_type);
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);

    const url =
      EndPoints.SELECTION_ORDERS.HISTORY_SELECTION_ORDER.replace(':id', id.toString()) +
      (q.toString() ? `?${q.toString()}` : '');

    return doGet({
      ...events,
      url,
      setFetching: setFetchingGetOrderHistory,
    });
  };

  // ---------- INCIDENCIAS de la orden ----------
  const getOrderIncidents = (
    orderId: number,
    params: { page?: number; page_size?: number; status?: string; reason?: string; date_from?: string; date_to?: string; q?: string } = {},
    events: ServiceEvents = {}
  ) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') q.set(k, String(v)); });
    const url =
      EndPoints.SELECTION_ORDER_INCIDENTS.GET_SELECTION_ORDER_INCIDENTS.replace(':id', String(orderId)) +
      (q.toString() ? `?${q}` : '');
    return doGet({ ...events, url, setFetching: setFetchingGetOrderIncidents });
  };

  const storeOrderIncident = (
    orderId: number,
    body: FormData | {
      date: string;
      time: string;
      reason_id: number;
      description: string;
      solution?: string;
      status?: 'OPEN' | 'CLOSED';
    },
    events: ServiceEvents = {}
  ) =>
    doPost({
      ...events,
      url: EndPoints.SELECTION_ORDER_INCIDENTS.STORE_SELECTION_ORDER_INCIDENT.replace(':id', String(orderId)),
      body,
      setFetching: setFetchingStoreOrderIncident,
    });

  const updateOrderIncident = (
    incidentId: number,
    body: FormData | {
      date?: string;
      time?: string;
      reason_id?: number;
      description?: string;
      solution?: string | null;
      status?: 'OPEN' | 'CLOSED';
      remove_evidence?: boolean;
    },
    events: ServiceEvents = {}
  ) => {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    return doPut({
      ...events,
      url: EndPoints.SELECTION_ORDER_INCIDENTS.UPDATE_SELECTION_ORDER_INCIDENT
              .replace(':incident_id', String(incidentId)),
      body,
      requestType: isFormData ? 'multipart' : 'json',
      setFetching: setFetchingUpdateOrderIncident,
    });
  };

  // Eliminar (DELETE soft)
  const deleteOrderIncident = (incidentId: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.SELECTION_ORDER_INCIDENTS.DELETE_SELECTION_ORDER_INCIDENT.replace(':incident_id', String(incidentId)),
      setFetching: setFetchingDeleteOrderIncident,
    });

  const storeOrderBatchByBarcode = (
    orderId: number,
    data: { barcode: string },
    events: ServiceEvents = {}
  ) =>
    doPost({
      ...events,
      url: EndPoints.SELECTION_ORDER_BATCHES.STORE_SELECTION_ORDER_BATCH_BY_BARCODE.replace(':id', orderId.toString()),
      body: data,
      setFetching: setFetchingStoreOrderBatchByBarcode,
    });

  // ---------- DESTINATION BATCHES ----------
  const getDestinationBatches = (orderId: number, events: ServiceEvents = {}) =>
    doGet({
      ...events,
      url: EndPoints.SELECTION_ORDER_DESTINATION_BATCHES.GET_DESTINATION_BATCHES.replace(':id', String(orderId)),
      setFetching: setFetchingGetDestinationBatches,
    });

  const createDestinationBatchOptions = (
    orderId: number,
    events: ServiceEvents = {},
    originLinkId?: number
  ) => {
    const baseUrl = EndPoints.SELECTION_ORDER_DESTINATION_BATCHES.CREATE_DESTINATION_BATCH_OPTIONS.replace(':id', String(orderId));
    const url = originLinkId != null ? `${baseUrl}?origin_link_id=${originLinkId}` : baseUrl;
    return doGet({
      ...events,
      url,
      setFetching: setFetchingCreateDestinationBatchOptions,
    });
  };

  const storeDestinationBatch = (
    orderId: number,
    data: { origin_link_id?: number | null; destination_batch_id: number; quantity: number; product_id?: number; real_date?: string },
    events: ServiceEvents = {}
  ) =>
    doPost({
      ...events,
      url: EndPoints.SELECTION_ORDER_DESTINATION_BATCHES.STORE_DESTINATION_BATCH.replace(':id', String(orderId)),
      body: data,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      requestType: 'json',
      setFetching: setFetchingStoreDestinationBatch,
    });

  const deleteDestinationBatch = (destId: number, events: ServiceEvents = {}) =>
    doDelete({
      ...events,
      url: EndPoints.SELECTION_ORDER_DESTINATION_BATCHES.DELETE_DESTINATION_BATCH.replace(':dest_id', String(destId)),
      setFetching: setFetchingDeleteDestinationBatch,
    });

  const voidDestinationBatch = (
    destId: number,
    events: ServiceEvents = {},
    body?: { real_date?: string }
  ) =>
    doPost({
      ...events,
      url: EndPoints.SELECTION_ORDER_DESTINATION_BATCHES.VOID_DESTINATION_BATCH.replace(':dest_id', String(destId)),
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      requestType: 'json',
      body: body ?? {},
      setFetching: setFetchingDeleteDestinationBatch,
    });

  return {
    // flags - orders
    fetchingGetSelectionOrders,
    fetchingCreateSelectionOrder,
    fetchingStoreSelectionOrder,
    fetchingEditSelectionOrder,
    fetchingUpdateSelectionOrder,
    fetchingDeleteSelectionOrder,
    fetchingActiveSelectionOrder,
    fetchingShowSelectionOrder,

    // flags - batches
    fetchingGetOrderBatches,
    fetchingCreateOrderBatchOptions,
    fetchingStoreOrderBatch,
    fetchingDeleteOrderBatch,
    fetchingStoreOrderBatchByBarcode,
    fetchingUpdateBatchStatus,
    
    // flags - load batches
    fetchingCreateLoadBatchOptions,
    fetchingStoreLoadBatch,

    // flags - collectors
    fetchingGetOrderCollectors,
    fetchingCreateOrderCollectorOptions,
    fetchingStoreOrderCollector,
    fetchingDeleteOrderCollector,

    // flag - history
    fetchingGetOrderHistory,

    // flags - incidents
    fetchingGetOrderIncidents,
    fetchingStoreOrderIncident,
    fetchingUpdateOrderIncident,
    fetchingDeleteOrderIncident,

    // métodos - orders
    getSelectionOrders,
    createSelectionOrder,
    storeSelectionOrder,
    editSelectionOrder,
    updateSelectionOrder,
    deleteSelectionOrder,
    activeSelectionOrder,
    showSelectionOrder,

    // métodos - batches
    getOrderBatches,
    createOrderBatchOptions,
    storeOrderBatch,
    deleteOrderBatch,
    storeOrderBatchByBarcode,
    updateBatchStatus,
    
    // métodos - load batches
    createLoadBatchOptions,
    storeLoadBatch,

    // flags - destination batches
    fetchingGetDestinationBatches,
    fetchingCreateDestinationBatchOptions,
    fetchingStoreDestinationBatch,
    fetchingDeleteDestinationBatch,

    // métodos - destination batches
    getDestinationBatches,
    createDestinationBatchOptions,
    storeDestinationBatch,
    deleteDestinationBatch,
    voidDestinationBatch,

    // métodos - collectors
    getOrderCollectors,
    createOrderCollectorOptions,
    storeOrderCollector,
    deleteOrderCollector,

    // método - history
    getSelectionOrderHistory,

    // métodos - incidents
    getOrderIncidents,
    storeOrderIncident,
    updateOrderIncident,
    deleteOrderIncident,
  };
};

export default useSelectionOrderService;